import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { ArrowRight, Info, Mail, Sparkles } from "lucide-react";
import { SelectOptionMappingRows } from "./SelectOptionMappingRows";
import { CustomFieldRow } from "./CustomFieldRow";
import { useOpportunitySettingsStore } from "@/store/opportunitySettingsStore";
import { isOwnerFieldType, type CustomFieldType } from "@/lib/customFieldTypes";
import { cn } from "@/lib/utils";
import {
  areCustomFieldMappingsEqual,
  autoMapCustomFieldMapping,
  getFieldEntries,
  getMappedErpProperty,
  type CustomFieldMappingValue,
  type MappingSchema,
} from "./customFieldMappingHelpers";
import type {
  ConnectionCapabilities,
  CustomFieldsSchema,
  ERPProviderKey,
} from "@forgepoint/shared";

export type {
  CustomFieldMappingValue,
  SelectFieldMappingValue,
} from "./customFieldMappingHelpers";

interface CustomFieldMappingSectionProps {
  /** Section heading. */
  title?: string;
  /** Current opportunityCustomFieldMapping from the form config */
  mapping: Record<string, CustomFieldMappingValue>;
  /** Called when a mapping changes */
  onMappingChange: (mapping: Record<string, CustomFieldMappingValue>) => void;
  /** The custom field mapping schema from the resolved config (has enums per field) */
  mappingSchema: MappingSchema | null;
  sourceSchema?: CustomFieldsSchema | null;
  /** Capability flags derived from granted OAuth scopes; null for non-HubSpot providers. */
  capabilities?: ConnectionCapabilities | null;
  /** The active ERP provider ID for provider-specific hints. */
  providerId?: ERPProviderKey | null;
}

/**
 * Renders a mapping row per Forgepoint custom field, each with a grouped
 * dropdown of compatible HubSpot deal properties. Options and grouping are
 * derived from the schema's `enum`/`enumNames` (format: `[groupName] Label`).
 * For select fields, also renders per-option value mapping dropdowns.
 * Hidden when no custom fields are configured for the org.
 * @param props - CustomFieldMappingSectionProps
 * @returns The mapping section element, or null when there are no custom fields
 */
export function CustomFieldMappingSection({
  title = "Custom Field Mapping",
  mapping,
  onMappingChange,
  mappingSchema,
  sourceSchema,
  capabilities,
  providerId,
}: CustomFieldMappingSectionProps) {
  const customFieldsSchema = useOpportunitySettingsStore(
    (s) => s.settings?.customFieldsSchema ?? null,
  );
  const fetchSettings = useOpportunitySettingsStore((s) => s.fetchSettings);
  const usesOpportunitySettings =
    providerId === "hubspot" || providerId === "salesforce";
  const allowsManualOptionValues =
    providerId === "epicor" ||
    providerId === "eniteo" ||
    providerId === "jobboss2";

  useEffect(() => {
    if (usesOpportunitySettings) {
      fetchSettings();
    }
  }, [fetchSettings, usesOpportunitySettings]);

  const fieldEntries = useMemo(
    () =>
      getFieldEntries(
        mappingSchema,
        sourceSchema?.properties ??
          (usesOpportunitySettings
            ? (customFieldsSchema?.properties ?? null)
            : null),
      ),
    [mappingSchema, sourceSchema, customFieldsSchema, usesOpportunitySettings],
  );

  const mappedCount = fieldEntries.filter(
    (e) => e.isLocked || getMappedErpProperty(mapping[e.key]) !== "",
  ).length;

  const autoMappedMapping = useMemo(
    () => autoMapCustomFieldMapping({ fieldEntries, mapping }),
    [fieldEntries, mapping],
  );
  const canAutoMap = !areCustomFieldMappingsEqual(mapping, autoMappedMapping);

  const [pendingUserMapping, setPendingUserMapping] = useState<{
    fieldKey: string;
    fieldLabel: string;
    value: string;
  } | null>(null);

  const handleFieldSelect = useCallback(
    (
      key: string,
      value: string,
      fieldType: CustomFieldType,
      isSelectField: boolean,
      label: string,
    ) => {
      if (!value) {
        const { [key]: _, ...rest } = mapping;
        onMappingChange(rest);
        return;
      }

      const wasPreviouslyMapped = !!mapping[key];
      if (isOwnerFieldType(fieldType) && !wasPreviouslyMapped) {
        setPendingUserMapping({ fieldKey: key, fieldLabel: label, value });
        return;
      }

      // Select fields use the object mapping shape
      if (isSelectField) {
        onMappingChange({
          ...mapping,
          [key]: { erpProperty: value, optionMapping: {} },
        });
        return;
      }

      onMappingChange({ ...mapping, [key]: value });
    },
    [mapping, onMappingChange],
  );

  const handleAutoMap = useCallback(() => {
    if (!canAutoMap) {
      return;
    }
    onMappingChange(autoMappedMapping);
  }, [autoMappedMapping, canAutoMap, onMappingChange]);

  const handleConfirmUserMapping = useCallback(() => {
    if (!pendingUserMapping) {
      return;
    }
    onMappingChange({
      ...mapping,
      [pendingUserMapping.fieldKey]: pendingUserMapping.value,
    });
    setPendingUserMapping(null);
  }, [pendingUserMapping, mapping, onMappingChange]);

  const handleOptionMappingChange = useCallback(
    (
      fieldKey: string,
      erpProperty: string,
      optionMapping: Record<string, string>,
    ) => {
      onMappingChange({
        ...mapping,
        [fieldKey]: { erpProperty, optionMapping },
      });
    },
    [mapping, onMappingChange],
  );

  if (fieldEntries.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3 border-t pt-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </span>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2 text-[11px]"
            onClick={handleAutoMap}
            disabled={!canAutoMap}
          >
            <Sparkles className="h-3 w-3" />
            Auto-map
          </Button>
          <span
            className={cn(
              "rounded-full border px-2 py-0.5 text-[11px] tabular-nums transition-colors",
              mappedCount === fieldEntries.length && mappedCount > 0
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "text-muted-foreground",
            )}
          >
            {mappedCount}/{fieldEntries.length}
          </span>
        </div>
      </div>

      {fieldEntries.map((entry) => {
        const mappingValue = mapping[entry.key];
        const selectedErpProperty =
          getMappedErpProperty(mappingValue) ||
          (entry.isLocked ? (entry.defaultTarget ?? "") : "");
        const currentOptionMapping =
          typeof mappingValue === "object" && mappingValue !== null
            ? mappingValue.optionMapping
            : {};
        const erpOptions =
          entry.isSelectField && selectedErpProperty
            ? (entry.erpPropertyOptions[selectedErpProperty] ?? [])
            : [];
        const targetsUsedByOtherFields = new Set(
          Object.entries(mapping)
            .filter(([key]) => key !== entry.key)
            .map(([, value]) => getMappedErpProperty(value))
            .filter(Boolean),
        );
        const availableOptions = entry.options.filter(
          (option) =>
            option.value === selectedErpProperty ||
            !targetsUsedByOtherFields.has(option.value),
        );

        return (
          <div key={entry.key} className="grid gap-2">
            <CustomFieldRow
              fieldKey={entry.key}
              label={entry.label}
              fieldType={entry.fieldType}
              options={availableOptions}
              selectedValue={selectedErpProperty}
              isMapped={!!selectedErpProperty}
              onSelect={(value) =>
                handleFieldSelect(
                  entry.key,
                  value,
                  entry.fieldType,
                  entry.isSelectField,
                  entry.label,
                )
              }
              disabled={
                entry.isLocked ||
                (isOwnerFieldType(entry.fieldType) &&
                  !!capabilities &&
                  !capabilities.canResolveOwners) ||
                (entry.fieldType === "attachments" &&
                  !!capabilities &&
                  !capabilities.canPushAttachments)
              }
            />
            {isOwnerFieldType(entry.fieldType) &&
              !!capabilities &&
              !capabilities.canResolveOwners && (
                <div className="flex animate-in fade-in duration-300 items-start gap-1.5 text-xs text-muted-foreground">
                  <Info className="mt-0.5 h-3 w-3 shrink-0" />
                  <span>
                    Owner fields will not sync — the Owners (read) scope was not
                    granted.
                  </span>
                </div>
              )}
            {entry.fieldType === "attachments" &&
              !!capabilities &&
              !capabilities.canPushAttachments && (
                <div className="flex animate-in fade-in duration-300 items-start gap-1.5 text-xs text-muted-foreground">
                  <Info className="mt-0.5 h-3 w-3 shrink-0" />
                  <span>
                    Attachment fields will not sync — the Files scope was not
                    granted.
                  </span>
                </div>
              )}
            {!entry.isLocked &&
              selectedErpProperty &&
              entry.fieldHints[selectedErpProperty] && (
                <div className="flex animate-in fade-in duration-300 items-start gap-1.5 text-xs text-muted-foreground">
                  <Info className="mt-0.5 h-3 w-3 shrink-0" />
                  <span>{entry.fieldHints[selectedErpProperty]}</span>
                </div>
              )}
            {entry.isSelectField &&
              !entry.isLocked &&
              selectedErpProperty &&
              (erpOptions.length > 0 || allowsManualOptionValues) && (
                <SelectOptionMappingRows
                  fpOptions={entry.fpFieldOptions}
                  erpOptions={erpOptions}
                  allowManualValues={allowsManualOptionValues}
                  passThroughWhenEmpty={allowsManualOptionValues}
                  optionMapping={currentOptionMapping}
                  onOptionMappingChange={(newOptionMapping) =>
                    handleOptionMappingChange(
                      entry.key,
                      selectedErpProperty,
                      newOptionMapping,
                    )
                  }
                />
              )}
          </div>
        );
      })}

      <AlertDialog
        open={pendingUserMapping !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingUserMapping(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>User field mapping</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  &ldquo;{pendingUserMapping?.fieldLabel}&rdquo; will sync by
                  matching email addresses between systems.
                </p>

                <div className="flex items-center gap-3 rounded-md border bg-muted/50 px-3 py-2.5 text-sm">
                  <div className="flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5 shrink-0 text-foreground/70" />
                    <span className="font-medium text-foreground">
                      Forgepoint user email
                    </span>
                  </div>
                  <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <div className="flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5 shrink-0 text-foreground/70" />
                    <span className="font-medium text-foreground">
                      {providerId === "salesforce"
                        ? "Salesforce user email"
                        : "HubSpot owner email"}
                    </span>
                  </div>
                </div>

                <p className="flex items-start gap-1.5 text-xs">
                  <Info className="mt-0.5 h-3 w-3 shrink-0" />
                  {providerId === "salesforce"
                    ? "If no Salesforce user matches the email, this field will be skipped during sync."
                    : "If no HubSpot owner matches the email, this field will be skipped during sync."}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmUserMapping}>
              Confirm mapping
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
